import type { Options } from './buffered-requests.service';

type Subscriber = { resolve; params? };

type Buffer = {
  args: any;
  timeoutId: number | null;

  /**
   * Inscritos que contêm a promesa que resolve a requisição bufferizada
   */
  subscribers: Subscriber[];
};

// TODO: O BufferedRequest e o PatchBufferedRequest se diferem no modo como tratam o buffer quando existe uma requisição em andamento. Podemos avaliar a implementação para tentar colocar esses modos em um só serviço definindo uma configuração.
export class BufferedRequests<ExtraAtrributesType = any> {
  private _bufferedRequests: { [bufferIdentifier: string]: Buffer & ExtraAtrributesType };

  constructor() {
    this._bufferedRequests = {};
  }

  tryAddBuffer(identifier: string, extraAttrs?: ExtraAtrributesType): void {
    if (!this._bufferedRequests[identifier]) {
      this._bufferedRequests[identifier] = {
        args: null,
        timeoutId: null,
        subscribers: [],
        ...(extraAttrs ?? ({} as any)),
      };
    }
  }

  removeBuffer(identifier: string): void {
    delete this._bufferedRequests[identifier];
  }

  addSubscriber(identifier: string, subscriber: Subscriber): void {
    this._bufferedRequests[identifier].subscribers.push(subscriber);
  }

  resolveSubscribers(
    subscribers: Subscriber[],
    response: any,
    splitResponse?: (args: any, response: any) => any,
  ): void {
    subscribers.forEach(async (sub) => {
      sub.resolve(splitResponse ? splitResponse(sub.params, await response) : await response);
    });
  }

  hasBuffer(identifier: string): boolean {
    return !!this._bufferedRequests[identifier];
  }

  hasRequestInDebounce(identifier: string, currentTimeoutId: number): boolean {
    return this._bufferedRequests[identifier].timeoutId !== currentTimeoutId;
  }

  getArgs<T>(identifier: string): T {
    return this._bufferedRequests[identifier].args;
  }

  getSubscribers(identifier: string): any {
    return this._bufferedRequests[identifier].subscribers;
  }

  setMergedArgs(identifier: string, newArgs: any, mergeFn: Options['mergeArgs']): void {
    let mergedArgs = newArgs;

    const oldArgs = this.getArgs(identifier);
    if (oldArgs !== null && oldArgs !== undefined) {
      mergedArgs = mergeFn(oldArgs, newArgs);
    }
    this._bufferedRequests[identifier].args = mergedArgs;
  }

  clearArgs(identifier: string): void {
    this._bufferedRequests[identifier].args = null;
  }

  clearSubscribers(identifier: string): void {
    this._bufferedRequests[identifier].subscribers = [];
  }

  getTimeoutId(identifier: string): number {
    return this._bufferedRequests[identifier].timeoutId;
  }

  setTimeoutId(identifier: string, timeoutId: number): void {
    this._bufferedRequests[identifier].timeoutId = timeoutId;
  }

  clearTimeout(identifier: string): void {
    clearTimeout(this._bufferedRequests[identifier].timeoutId);
  }

  getExtraAttribute(identifier: string, extraAttrKey: keyof ExtraAtrributesType): any {
    return this._bufferedRequests[identifier][extraAttrKey];
  }

  setExtraAttribute(identifier: string, extraAttrKey: keyof ExtraAtrributesType, extraAttrValue: any): any {
    this._bufferedRequests[identifier][extraAttrKey] = extraAttrValue;
  }
}
